import React, { useCallback, useEffect, useRef, useState } from 'react';
import trackingIntakeService from '../services/trackingIntakeService';
import useShippingRequest from '../hooks/useShippingRequest';
import { useT } from '../i18n/LanguageContext';

export default function TrackingIntakeReview({ onApplied }) {
  const t = useT();
  const [page, setPage] = useState(1);
  const [outcome, setOutcome] = useState('review');
  const [totalPages, setTotalPages] = useState(1);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');
  const lock = useRef(false);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState(null);
  const [orderNumber, setOrderNumber] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [stale, setStale] = useState(false);
  const { begin, version } = useShippingRequest(`tracking-review:${outcome}:${page}`);
  const load = useCallback(async () => {
    const request = begin();
    lock.current = false; setBusy(''); setLoading(true); setError(''); setRows([]); setSelected(null); setStale(false);
    try {
      const result = await trackingIntakeService.reviewQueue(page, request, outcome);
      if (request.isCurrent()) { setRows(result.data); setTotalPages(result.totalPages); }
    } catch { if (request.isCurrent()) setError('Could not load tracking reviews. Please try again.'); }
    finally { if (request.isCurrent()) setLoading(false); }
  }, [begin, page, outcome]);
  useEffect(() => { load(); }, [load, version]);
  const decide = async action => {
    if (lock.current || stale || !selected || !selected.allowedActions.includes(action) ||
        (action === 'apply' && (!confirmed || !orderNumber.trim() || selected.resolvedExternally))) return;
    lock.current = true; setBusy(action); setError(''); setStale(true);
    const request = begin();
    try {
      await trackingIntakeService.decide(selected, action, orderNumber, request);
      if (request.isCurrent()) {
        setRows(current => current.filter(row => row._id !== selected._id));
        setSelected(null); setConfirmed(false); setStale(false);
        if (action === 'apply') onApplied?.();
      }
    } catch (failure) {
      if (request.isCurrent()) setError(failure?.response?.status === 409 ?
        'This tracking review changed. Refresh and review it again.' :
        'Could not confirm the tracking review. Refresh before trying again.');
    } finally { if (request.isCurrent()) { lock.current = false; setBusy(''); } }
  };
  return <div>
    <p className="mb-3 text-sm text-slate-600">{t('Verify the order and shipment before applying a tracking notice. Dismiss notices that no longer need action.')}</p>
    <div role="tablist" aria-label={t('Tracking review status')} className="mb-3 flex flex-wrap gap-2">
      {[['review', 'Needs review'], ['would_apply', 'Shadow matches']].map(([value, label]) =>
        <button type="button" role="tab" key={value} aria-selected={outcome === value} disabled={!!busy} onClick={() => { setOutcome(value); setPage(1); }}
          className={`rounded-lg px-3 py-2 text-sm ${outcome === value ? 'bg-blue-50 text-[#0089ff]' : 'text-slate-600'}`}>{t(label)}</button>)}
    </div>
    {outcome === 'would_apply' && <p className="mb-3 text-sm text-slate-600">{t('Shadow matches have not changed any orders. Applying one will update the selected order.')}</p>}
    <button type="button" disabled={loading || !!busy} onClick={load} className="mb-3 text-sm text-[#0089ff]">{t('Refresh reviews')}</button>
    {error && <p role="alert" className="mb-3 text-sm text-red-700">{t(error)}</p>}
    {loading ? <p role="status">{t('Loading tracking reviews...')}</p> : !selected ? <>
      {!rows.length && !error && <p className="text-sm text-slate-500">{t('No tracking notices need review.')}</p>}
      <ul className="space-y-2">{rows.map(row => <li key={row._id} className="flex items-center justify-between gap-3 rounded-lg border p-3">
        <div className="min-w-0 text-sm"><p className="break-all">{row.trackingNumber || t('Tracking unavailable')}</p>
          <p className="text-xs text-slate-500">{t(row.resolvedExternally ? 'Already recorded on a shipment' : row.reason)}</p></div>
        <button type="button" disabled={!!busy} onClick={() => { setSelected(row); setOrderNumber(''); setConfirmed(false); setStale(false); setError(''); }}
          className="shrink-0 text-sm text-[#0089ff]">{t('Review tracking')}</button>
      </li>)}</ul>
      {totalPages > 1 && <div className="mt-3 flex justify-end gap-3 text-sm">
        <button type="button" disabled={page <= 1} onClick={() => setPage(value => value - 1)}>{t('Previous')}</button>
        <span>{t('Page {page} of {total}', { page, total: totalPages })}</span>
        <button type="button" disabled={page >= totalPages} onClick={() => setPage(value => value + 1)}>{t('Next')}</button>
      </div>}
    </> : <div className="space-y-4 rounded-lg border p-4">
      <button type="button" disabled={!!busy || stale} onClick={() => setSelected(null)} className="text-sm text-[#0089ff]">{t('Back to reviews')}</button>
      <p className="break-all text-sm font-medium">{selected.trackingNumber || t('Tracking unavailable')}</p>
      <p className="text-sm text-slate-600">{t(selected.resolvedExternally ? 'Already recorded on a shipment' : selected.reason)}</p>
      {selected.allowedActions.includes('apply') && !selected.resolvedExternally && <>
        {!!selected.candidates.length && <p className="text-sm text-slate-600">{t('Possible orders: {orders}', { orders: selected.candidates.map(row => row.orderId).join(', ') })}</p>}
        <label className="block text-sm">{t('Order number')}
          <input value={orderNumber} maxLength={64} disabled={!!busy || stale} onChange={event => { setOrderNumber(event.target.value); setConfirmed(false); }}
            className="mt-1 block w-full rounded-lg border px-3 py-2" />
        </label>
        <label className="flex items-start gap-2 text-sm"><input type="checkbox" checked={confirmed} disabled={!!busy || stale || !orderNumber.trim()}
          onChange={event => setConfirmed(event.target.checked)} className="mt-1" />
          {t('I verified this tracking notice belongs to the entered order.')}
        </label>
      </>}
      <div className="flex flex-wrap gap-3">
        {selected.allowedActions.includes('apply') && !selected.resolvedExternally && <button type="button" disabled={!!busy || stale || !confirmed || !orderNumber.trim()}
          onClick={() => decide('apply')} className="rounded-lg bg-[#0089ff] px-3 py-2 text-sm text-white disabled:opacity-50">{t(selected.event === 'delivered' ? 'Confirm delivery' : 'Apply tracking')}</button>}
        {selected.allowedActions.includes('dismiss') && <button type="button" disabled={!!busy || stale} onClick={() => decide('dismiss')}
          className="rounded-lg border px-3 py-2 text-sm disabled:opacity-50">{t('Dismiss review')}</button>}
      </div>
    </div>}
    {!!busy && <p role="status" className="mt-3 text-sm text-slate-500">{t('Updating tracking review...')}</p>}
  </div>;
}
